import cn from 'classnames';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import ru from 'date-fns/locale/ru';
import parseISO from 'date-fns/parseISO';
import React, { forwardRef, memo } from 'react';
import * as schedule from '@pages/shedules/styles.module.scss';
import { ArrowIosLeft, ArrowIosRight } from '@shared/assets/index';
import { IconButton } from '@shared/ui/index';
// import isNow from '@src/library/helpers/schedules/isNow';
import { isNow, isNow15, useDate, useWeekLoader } from './model';
import * as st from './styles.module.scss';

type WithClassName = {
  className?: string;
};

interface DayProps {
  date: Date;
}

interface HourProps {
  date: string;
}

interface ColumnProps extends React.HTMLAttributes<HTMLDivElement>, WithClassName {
  children?: React.ReactNode;
}

interface SessionProps {
  children?: React.ReactNode;
  className?: string;
}

interface WeekProps extends React.HTMLAttributes<HTMLDivElement> {
  date: Date;
  children?: React.ReactNode;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

const Day: React.FC<DayProps> = ({ date }) => {
  return (
    <div className={cn(st.day, isToday(date) && st.today)}>
      {format(date, 'EEEEEE', { locale: ru })}
      <div className={cn(st.day__date, isToday(date) && st.today)}>{format(date, 'dd')}</div>
    </div>
  );
};

const Hour: React.FC<HourProps> = memo(({ date }) => {
  const scroll = React.useRef<any>(null);

  React.useEffect(() => {
    const isoDate = parseISO(date);
    if (isNow(isoDate) || format(isoDate, 'HH:mm') === '09:00') {
      scroll.current && scroll.current.scrollIntoView();
    }
  }, [scroll, date]);

  return (
    <div ref={scroll} className={cn(st.hour, isNow15(parseISO(date)) && st.active)}>
      {format(parseISO(date), 'HH:mm').split(':')[1] === '00' ? (
        <div className={st.black}>{format(parseISO(date), 'HH:mm')}</div>
      ) : (
        <div className={cn(st.gray, isNow15(parseISO(date)) && st.active)}>
          {format(parseISO(date), ':mm')}
        </div>
      )}
    </div>
  );
});

export const Column: React.FC<ColumnProps> = memo(({ children, className, ...props }) => {
  return (
    <div className={cn(st.calendar__column, className)} {...props}>
      {children}
    </div>
  );
});

export const Session: React.FC<SessionProps> = memo(({ children, className }) => {
  return <div className={cn(st.calendar__session, className)}>{children}</div>;
});

const Loader: React.FC = () => {
  const loader = [];
  const day = new Array(96).fill(' ');

  for (let i = 0; i < 7; i++) loader.push(day);

  return (
    <>
      {loader.map((col, dayIndex) => (
        <div
          key={dayIndex}
          className={cn(
            st.calendar__column,
            // отступ между понедельник - пятница и суббота - воскресенье
            dayIndex === 5 && schedule.saturday
          )}
        >
          {col.map((block, n) => (
            <div key={n} className={st.calendar__session}>
              <div className={st.calendar__session_fake} />
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

export const Week = forwardRef<any, WeekProps>(
  ({ date, children, className, onNextPage, onPreviousPage, ...props }, ref) => {
    const { week, hours } = useDate(date);
    const { isLoading } = useWeekLoader(date);

    const days = week.map((date, index) => (
      <div
        className={cn(st.weekDay, index === 5 && st.saturdayAndSunday)}
        key={format(date, 'EEEEEE dd')}
      >
        <Day date={date} />
      </div>
    ));
    return (
      <div className={cn(st.week, className)} {...props}>
        <section className={st.weekdays}>
          <div className={st.days}>{days}</div>
          <span className={st.currentMonth}>{format(date, 'LLLL yyyy', { locale: ru })}</span>
          <div className={st.buttons}>
            {onPreviousPage && (
              <IconButton onClick={onPreviousPage} size="small" variant="dark">
                <ArrowIosLeft />
              </IconButton>
            )}

            {onNextPage && (
              <IconButton onClick={onNextPage} size="small" variant="dark">
                <ArrowIosRight />
              </IconButton>
            )}
          </div>
        </section>
        <div className={cn(st.days, st.daysMobile)}>{days}</div>
        <section ref={ref} className={st.calendar}>
          <div className={st.calendar__hours}>
            {hours.map((hour) => (
              <Hour key={hour} date={hour} />
            ))}
          </div>
          <div className={st.calendar__sessions}>{isLoading ? <Loader /> : children}</div>
        </section>
      </div>
    );
  }
);
